import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, 
  ArrowDown, 
  Play, 
  Pause, 
  RotateCcw, 
  BookOpen, 
  GraduationCap,
  FileText,
  ShoppingCart,
  Package,
  Factory,
  Truck,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  Info,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { formatDateTime } from '../utils/formatters';

const TypicalFlowFeatures = () => {
  const [activeFlow, setActiveFlow] = useState('sales-cycle');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showDetails, setShowDetails] = useState(true);
  const [autoPlaySpeed, setAutoPlaySpeed] = useState(2000); // 2 seconds per step
  const [learningMode, setLearningMode] = useState('interactive'); // interactive, guided, exam

  // Typical ERP Business Flows
  const typicalFlows = {
    'sales-cycle': {
      title: '銷售週期流程',
      subtitle: 'Sales Cycle Management',
      description: '從客戶詢價到收款完成的完整銷售流程',
      icon: ShoppingCart,
      color: 'blue',
      steps: [
        {
          id: 1,
          name: '客戶詢價',
          module: 'CRM',
          description: '客戶通過各種渠道提出產品或服務詢價',
          dataFlow: '客戶資料 → 詢價記錄',
          duration: '1-3天',
          status: 'pending',
          details: {
            inputs: ['客戶資訊', '產品需求', '預算範圍'],
            outputs: ['詢價單', '初步報價'],
            systems: ['CRM系統', '產品目錄'],
            documents: ['客戶檔案', '詢價記錄']
          }
        },
        {
          id: 2,
          name: '報價準備',
          module: 'Quotation',
          description: '根據客戶需求準備詳細報價單',
          dataFlow: '詢價資料 → 報價單',
          duration: '1-2天',
          status: 'pending',
          details: {
            inputs: ['詢價資料', '產品規格', '成本分析'],
            outputs: ['正式報價單', '技術規格書'],
            systems: ['報價系統', '成本核算系統'],
            documents: ['報價單', '技術文件']
          }
        },
        {
          id: 3,
          name: '客戶確認',
          module: 'Sales',
          description: '客戶確認報價並同意購買',
          dataFlow: '報價單 → 銷售訂單',
          duration: '1-7天',
          status: 'pending',
          details: {
            inputs: ['客戶確認', '付款條件', '交貨要求'],
            outputs: ['銷售訂單', '客戶確認書'],
            systems: ['銷售系統', '合同管理'],
            documents: ['銷售訂單', '客戶確認書']
          }
        },
        {
          id: 4,
          name: '訂單處理',
          module: 'Order Management',
          description: '處理銷售訂單並安排生產或採購',
          dataFlow: '銷售訂單 → 生產/採購訂單',
          duration: '1-3天',
          status: 'pending',
          details: {
            inputs: ['銷售訂單', '庫存狀況', '生產能力'],
            outputs: ['生產訂單', '採購訂單'],
            systems: ['訂單管理', 'MRP系統'],
            documents: ['生產訂單', '採購訂單']
          }
        },
        {
          id: 5,
          name: '生產/採購',
          module: 'Production/Purchase',
          description: '執行生產計劃或採購流程',
          dataFlow: '訂單 → 成品/物料',
          duration: '7-30天',
          status: 'pending',
          details: {
            inputs: ['生產訂單', '物料需求', '供應商報價'],
            outputs: ['成品', '採購物料'],
            systems: ['生產管理', '採購系統'],
            documents: ['生產記錄', '採購單']
          }
        },
        {
          id: 6,
          name: '品質檢驗',
          module: 'Quality Control',
          description: '對產品進行品質檢驗和測試',
          dataFlow: '成品 → 檢驗報告',
          duration: '1-2天',
          status: 'pending',
          details: {
            inputs: ['成品', '檢驗標準', '測試設備'],
            outputs: ['檢驗報告', '合格證書'],
            systems: ['品質管理系統', '實驗室管理'],
            documents: ['檢驗報告', '品質證書']
          }
        },
        {
          id: 7,
          name: '出貨準備',
          module: 'Logistics',
          description: '準備產品出貨和物流安排',
          dataFlow: '合格產品 → 出貨單',
          duration: '1-2天',
          status: 'pending',
          details: {
            inputs: ['合格產品', '包裝材料', '物流資訊'],
            outputs: ['出貨單', '包裝清單'],
            systems: ['倉儲管理', '物流系統'],
            documents: ['出貨單', '包裝清單']
          }
        },
        {
          id: 8,
          name: '交付客戶',
          module: 'Delivery',
          description: '產品交付給客戶並確認收貨',
          dataFlow: '出貨單 → 客戶簽收',
          duration: '1-5天',
          status: 'pending',
          details: {
            inputs: ['出貨單', '運輸安排', '客戶資訊'],
            outputs: ['簽收單', '交付證明'],
            systems: ['運輸管理', '客戶服務'],
            documents: ['簽收單', '交付證明']
          }
        },
        {
          id: 9,
          name: '開立發票',
          module: 'Finance',
          description: '根據交付情況開立發票',
          dataFlow: '交付證明 → 發票',
          duration: '1天',
          status: 'pending',
          details: {
            inputs: ['交付證明', '價格資訊', '稅務資料'],
            outputs: ['發票', '應收帳款'],
            systems: ['財務系統', '稅務管理'],
            documents: ['發票', '應收帳款記錄']
          }
        },
        {
          id: 10,
          name: '收款處理',
          module: 'Finance',
          description: '處理客戶付款並更新財務記錄',
          dataFlow: '發票 → 收款記錄',
          duration: '30-90天',
          status: 'pending',
          details: {
            inputs: ['發票', '付款通知', '銀行記錄'],
            outputs: ['收款記錄', '財務報表'],
            systems: ['財務系統', '銀行系統'],
            documents: ['收款記錄', '財務報表']
          }
        }
      ]
    },
    'purchase-cycle': {
      title: '採購週期流程',
      subtitle: 'Purchase Cycle Management',
      description: '從需求識別到供應商付款的完整採購流程',
      icon: Package,
      color: 'green',
      steps: [
        {
          id: 1,
          name: '需求識別',
          module: 'MRP',
          description: '識別物料需求並制定採購計劃',
          dataFlow: '生產計劃 → 採購需求',
          duration: '1-2天',
          status: 'pending',
          details: {
            inputs: ['生產計劃', '庫存狀況', 'BOM清單'],
            outputs: ['採購需求', '物料清單'],
            systems: ['MRP系統', '庫存管理'],
            documents: ['採購需求單', '物料清單']
          }
        },
        {
          id: 2,
          name: '供應商選擇',
          module: 'Supplier Management',
          description: '評估和選擇合適的供應商',
          dataFlow: '採購需求 → 供應商清單',
          duration: '2-5天',
          status: 'pending',
          details: {
            inputs: ['採購需求', '供應商資料', '評估標準'],
            outputs: ['供應商清單', '評估報告'],
            systems: ['供應商管理', '評估系統'],
            documents: ['供應商清單', '評估報告']
          }
        },
        {
          id: 3,
          name: '詢價報價',
          module: 'Quotation',
          description: '向供應商詢價並收集報價',
          dataFlow: '採購需求 → 供應商報價',
          duration: '3-7天',
          status: 'pending',
          details: {
            inputs: ['採購需求', '技術規格', '供應商清單'],
            outputs: ['詢價單', '供應商報價'],
            systems: ['採購系統', '報價管理'],
            documents: ['詢價單', '報價比較表']
          }
        },
        {
          id: 4,
          name: '報價評估',
          module: 'Procurement',
          description: '評估供應商報價並選擇最佳方案',
          dataFlow: '供應商報價 → 選擇結果',
          duration: '1-3天',
          status: 'pending',
          details: {
            inputs: ['供應商報價', '技術評估', '成本分析'],
            outputs: ['評估報告', '供應商選擇'],
            systems: ['採購系統', '成本分析'],
            documents: ['評估報告', '選擇決策']
          }
        },
        {
          id: 5,
          name: '採購訂單',
          module: 'Purchase Order',
          description: '向選定供應商下達採購訂單',
          dataFlow: '供應商選擇 → 採購訂單',
          duration: '1天',
          status: 'pending',
          details: {
            inputs: ['供應商選擇', '價格條件', '交貨要求'],
            outputs: ['採購訂單', '合同條款'],
            systems: ['採購系統', '合同管理'],
            documents: ['採購訂單', '採購合同']
          }
        },
        {
          id: 6,
          name: '訂單跟蹤',
          module: 'Order Tracking',
          description: '跟蹤採購訂單的執行狀況',
          dataFlow: '採購訂單 → 執行狀態',
          duration: '持續跟蹤',
          status: 'pending',
          details: {
            inputs: ['採購訂單', '供應商回覆', '進度更新'],
            outputs: ['跟蹤報告', '狀態更新'],
            systems: ['訂單跟蹤', '供應商門戶'],
            documents: ['跟蹤報告', '進度更新']
          }
        },
        {
          id: 7,
          name: '收貨檢驗',
          module: 'Receiving',
          description: '接收物料並進行品質檢驗',
          dataFlow: '到貨 → 檢驗結果',
          duration: '1-2天',
          status: 'pending',
          details: {
            inputs: ['到貨通知', '檢驗標準', '檢驗設備'],
            outputs: ['檢驗報告', '收貨單'],
            systems: ['收貨管理', '品質檢驗'],
            documents: ['檢驗報告', '收貨單']
          }
        },
        {
          id: 8,
          name: '入庫管理',
          module: 'Inventory',
          description: '將合格物料入庫並更新庫存',
          dataFlow: '檢驗結果 → 庫存更新',
          duration: '1天',
          status: 'pending',
          details: {
            inputs: ['檢驗報告', '收貨單', '庫位資訊'],
            outputs: ['入庫單', '庫存更新'],
            systems: ['倉儲管理', '庫存系統'],
            documents: ['入庫單', '庫存記錄']
          }
        },
        {
          id: 9,
          name: '發票處理',
          module: 'Finance',
          description: '處理供應商發票並核對',
          dataFlow: '供應商發票 → 應付帳款',
          duration: '1-3天',
          status: 'pending',
          details: {
            inputs: ['供應商發票', '採購訂單', '收貨單'],
            outputs: ['應付帳款', '核對報告'],
            systems: ['財務系統', '應付帳款'],
            documents: ['應付帳款', '核對報告']
          }
        },
        {
          id: 10,
          name: '付款處理',
          module: 'Finance',
          description: '安排付款並更新財務記錄',
          dataFlow: '應付帳款 → 付款記錄',
          duration: '30-60天',
          status: 'pending',
          details: {
            inputs: ['應付帳款', '付款條件', '現金流'],
            outputs: ['付款記錄', '財務報表'],
            systems: ['財務系統', '銀行系統'],
            documents: ['付款記錄', '財務報表']
          }
        }
      ]
    },
    'production-cycle': {
      title: '生產週期流程',
      subtitle: 'Production Cycle Management',
      description: '從生產計劃到成品入庫的完整生產流程',
      icon: Factory,
      color: 'purple',
      steps: [
        {
          id: 1,
          name: '生產計劃',
          module: 'Production Planning',
          description: '制定生產計劃和排程',
          dataFlow: '銷售訂單 → 生產計劃',
          duration: '1-3天',
          status: 'pending',
          details: {
            inputs: ['銷售訂單', '生產能力', '物料可用性'],
            outputs: ['生產計劃', '排程表'],
            systems: ['生產計劃系統', 'MRP系統'],
            documents: ['生產計劃', '排程表']
          }
        },
        {
          id: 2,
          name: '物料準備',
          module: 'Material Preparation',
          description: '準備生產所需的物料和工具',
          dataFlow: '生產計劃 → 物料準備',
          duration: '1-2天',
          status: 'pending',
          details: {
            inputs: ['生產計劃', 'BOM清單', '庫存狀況'],
            outputs: ['物料清單', '工具準備'],
            systems: ['倉儲管理', '工具管理'],
            documents: ['物料清單', '工具清單']
          }
        },
        {
          id: 3,
          name: '生產排程',
          module: 'Scheduling',
          description: '安排生產線和人員配置',
          dataFlow: '生產計劃 → 生產排程',
          duration: '1天',
          status: 'pending',
          details: {
            inputs: ['生產計劃', '設備狀況', '人員配置'],
            outputs: ['生產排程', '人員安排'],
            systems: ['排程系統', '人員管理'],
            documents: ['生產排程', '人員安排表']
          }
        },
        {
          id: 4,
          name: '生產執行',
          module: 'Production Execution',
          description: '執行生產作業和品質控制',
          dataFlow: '生產排程 → 生產記錄',
          duration: '1-30天',
          status: 'pending',
          details: {
            inputs: ['生產排程', '作業指導', '品質標準'],
            outputs: ['生產記錄', '品質數據'],
            systems: ['生產執行系統', '品質管理'],
            documents: ['生產記錄', '品質報告']
          }
        },
        {
          id: 5,
          name: '品質檢驗',
          module: 'Quality Control',
          description: '對生產過程和產品進行品質檢驗',
          dataFlow: '生產記錄 → 品質報告',
          duration: '1-2天',
          status: 'pending',
          details: {
            inputs: ['生產記錄', '檢驗標準', '檢驗設備'],
            outputs: ['品質報告', '檢驗記錄'],
            systems: ['品質管理系統', '實驗室管理'],
            documents: ['品質報告', '檢驗記錄']
          }
        },
        {
          id: 6,
          name: '成品入庫',
          module: 'Inventory',
          description: '將合格成品入庫並更新庫存',
          dataFlow: '品質報告 → 庫存更新',
          duration: '1天',
          status: 'pending',
          details: {
            inputs: ['品質報告', '成品數量', '庫位資訊'],
            outputs: ['入庫單', '庫存更新'],
            systems: ['倉儲管理', '庫存系統'],
            documents: ['入庫單', '庫存記錄']
          }
        },
        {
          id: 7,
          name: '成本核算',
          module: 'Cost Accounting',
          description: '核算生產成本並分析效率',
          dataFlow: '生產記錄 → 成本報告',
          duration: '1-3天',
          status: 'pending',
          details: {
            inputs: ['生產記錄', '成本數據', '效率指標'],
            outputs: ['成本報告', '效率分析'],
            systems: ['成本核算系統', '分析工具'],
            documents: ['成本報告', '效率分析']
          }
        }
      ]
    },
    'financial-cycle': {
      title: '財務週期流程',
      subtitle: 'Financial Cycle Management',
      description: '從交易記錄到財務報表的完整財務流程',
      icon: DollarSign,
      color: 'orange',
      steps: [
        {
          id: 1,
          name: '交易記錄',
          module: 'General Ledger',
          description: '記錄所有業務交易和會計分錄',
          dataFlow: '業務交易 → 會計分錄',
          duration: '即時',
          status: 'pending',
          details: {
            inputs: ['業務交易', '會計科目', '金額'],
            outputs: ['會計分錄', '總帳記錄'],
            systems: ['總帳系統', '會計科目表'],
            documents: ['會計分錄', '總帳記錄']
          }
        },
        {
          id: 2,
          name: '應收管理',
          module: 'Accounts Receivable',
          description: '管理客戶應收帳款和收款',
          dataFlow: '銷售發票 → 應收帳款',
          duration: '30-90天',
          status: 'pending',
          details: {
            inputs: ['銷售發票', '收款記錄', '客戶資料'],
            outputs: ['應收帳款', '收款報告'],
            systems: ['應收帳款系統', '客戶管理'],
            documents: ['應收帳款', '收款報告']
          }
        },
        {
          id: 3,
          name: '應付管理',
          module: 'Accounts Payable',
          description: '管理供應商應付帳款和付款',
          dataFlow: '採購發票 → 應付帳款',
          duration: '30-60天',
          status: 'pending',
          details: {
            inputs: ['採購發票', '付款記錄', '供應商資料'],
            outputs: ['應付帳款', '付款報告'],
            systems: ['應付帳款系統', '供應商管理'],
            documents: ['應付帳款', '付款報告']
          }
        },
        {
          id: 4,
          name: '現金管理',
          module: 'Cash Management',
          description: '管理現金流和銀行帳戶',
          dataFlow: '現金交易 → 現金報告',
          duration: '即時',
          status: 'pending',
          details: {
            inputs: ['現金收入', '現金支出', '銀行記錄'],
            outputs: ['現金報告', '銀行對帳'],
            systems: ['現金管理系統', '銀行系統'],
            documents: ['現金報告', '銀行對帳單']
          }
        },
        {
          id: 5,
          name: '成本核算',
          module: 'Cost Accounting',
          description: '核算各項成本和費用',
          dataFlow: '成本數據 → 成本報告',
          duration: '月度',
          status: 'pending',
          details: {
            inputs: ['成本數據', '費用記錄', '分配標準'],
            outputs: ['成本報告', '費用分析'],
            systems: ['成本核算系統', '分析工具'],
            documents: ['成本報告', '費用分析']
          }
        },
        {
          id: 6,
          name: '財務報表',
          module: 'Financial Reporting',
          description: '編制財務報表和財務分析',
          dataFlow: '財務數據 → 財務報表',
          duration: '月度/季度',
          status: 'pending',
          details: {
            inputs: ['財務數據', '會計記錄', '分析要求'],
            outputs: ['財務報表', '財務分析'],
            systems: ['財務報表系統', '分析工具'],
            documents: ['財務報表', '財務分析']
          }
        }
      ]
    }
  };

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isPlaying && currentStep < typicalFlows[activeFlow].steps.length - 1) {
      interval = setInterval(() => {
        setCurrentStep(prev => prev + 1);
      }, autoPlaySpeed);
    } else if (currentStep >= typicalFlows[activeFlow].steps.length - 1) {
      setIsPlaying(false);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentStep, activeFlow, autoPlaySpeed]);

  // Reset flow when changing active flow
  useEffect(() => {
    setCurrentStep(0);
    setIsPlaying(false);
  }, [activeFlow]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'current':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'current':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const resetFlow = () => {
    setCurrentStep(0);
    setIsPlaying(false);
  };

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'pending';
  };

  const FlowIcon = typicalFlows[activeFlow].icon;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">典型ERP流程學習</h1>
              <p className="text-lg text-gray-600 mt-2">Typical ERP Flow Learning Center</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <BookOpen className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">學習模式</span>
              </div>
              <select
                value={learningMode}
                onChange={(e) => setLearningMode(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="interactive">互動學習</option>
                <option value="guided">引導學習</option>
                <option value="exam">測驗模式</option>
              </select>
            </div>
          </div>
        </div>

        {/* Flow Selection */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(typicalFlows).map(([key, flow]) => {
              const Icon = flow.icon;
              return (
                <button
                  key={key}
                  onClick={() => setActiveFlow(key)}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    activeFlow === key
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-full bg-${flow.color}-100`}>
                      <Icon className={`h-6 w-6 text-${flow.color}-600`} />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">{flow.title}</h3>
                      <p className="text-sm text-gray-600">{flow.subtitle}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Flow Details */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Flow Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white bg-opacity-20 rounded-full">
                  <FlowIcon className="h-8 w-8" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{typicalFlows[activeFlow].title}</h2>
                  <p className="text-blue-100">{typicalFlows[activeFlow].subtitle}</p>
                  <p className="text-blue-100 text-sm mt-1">{typicalFlows[activeFlow].description}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={togglePlay}
                  className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                >
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  <span>{isPlaying ? '暫停' : '播放'}</span>
                </button>
                <button
                  onClick={resetFlow}
                  className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                >
                  <RotateCcw className="h-4 w-4" />
                  <span>重置</span>
                </button>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                >
                  {showDetails ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  <span>{showDetails ? '隱藏詳情' : '顯示詳情'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Flow Steps */}
          <div className="p-6">
            <div className="space-y-4">
              {typicalFlows[activeFlow].steps.map((step, index) => {
                const status = getStepStatus(index);
                const isCurrent = index === currentStep;
                
                return (
                  <div
                    key={step.id}
                    className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${
                      isCurrent ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleStepClick(index)}
                  >
                    <div className="flex items-start space-x-4">
                      {/* Step Number */}
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                        status === 'completed' ? 'bg-green-100 text-green-600' :
                        status === 'current' ? 'bg-blue-100 text-blue-600' :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {step.id}
                      </div>

                      {/* Step Content */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{step.name}</h3>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(status)}`}>
                              {status === 'completed' ? '已完成' : status === 'current' ? '進行中' : '待處理'}
                            </span>
                            {getStatusIcon(status)}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-3">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">模組</p>
                            <p className="font-medium text-gray-900">{step.module}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">數據流</p>
                            <p className="font-medium text-gray-900">{step.dataFlow}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">預計時間</p>
                            <p className="font-medium text-gray-900">{step.duration}</p>
                          </div>
                        </div>

                        <p className="text-gray-700 mb-3">{step.description}</p>

                        {/* Detailed Information */}
                        {showDetails && (
                          <div className="bg-gray-50 rounded-lg p-4 mt-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div>
                                <h4 className="font-medium text-gray-900 mb-2">輸入資料</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {step.details.inputs.map((input, idx) => (
                                    <li key={idx} className="flex items-center">
                                      <ArrowRight className="h-3 w-3 mr-1 text-gray-400" />
                                      {input}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 mb-2">輸出結果</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {step.details.outputs.map((output, idx) => (
                                    <li key={idx} className="flex items-center">
                                      <ArrowRight className="h-3 w-3 mr-1 text-gray-400" />
                                      {output}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 mb-2">相關系統</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {step.details.systems.map((system, idx) => (
                                    <li key={idx} className="flex items-center">
                                      <ArrowRight className="h-3 w-3 mr-1 text-gray-400" />
                                      {system}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 mb-2">產生文件</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {step.details.documents.map((doc, idx) => (
                                    <li key={idx} className="flex items-center">
                                      <ArrowRight className="h-3 w-3 mr-1 text-gray-400" />
                                      {doc}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Learning Tips */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <GraduationCap className="h-6 w-6 text-blue-600 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">學習提示</h3>
              <ul className="text-blue-800 space-y-2">
                <li>• 點擊每個步驟查看詳細資訊</li>
                <li>• 使用播放功能自動瀏覽整個流程</li>
                <li>• 觀察數據如何在不同模組間流動</li>
                <li>• 注意每個步驟的輸入、輸出和相關系統</li>
                <li>• 理解ERP系統的整合性和數據一致性</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypicalFlowFeatures;
